import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const VOIP_TOOL_API_BASE = process.env.REACT_APP_VOIP_TOOL_API || 'http://localhost:3438';
const VOIP_AUTO_API_BASE = process.env.REACT_APP_VOIP_AUTO_API || 'https://frek.cc/proxy/38.54.86.24:8016';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0b0b10',
      paper: '#15161b',
    },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232, 233, 238, 0.72)',
    },
    primary: {
      main: '#90caf9',
    },
    success: {
      main: '#4caf50',
    },
    warning: {
      main: '#ffb74d',
    },
    error: {
      main: '#f44336',
    },
  },
});

function getToday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
}

function toNumber(value, rawFallback) {
  const direct = Number(value);
  if (Number.isFinite(direct)) return direct;
  const text = String(rawFallback || '');
  const match = text.match(/[-+]?\d[\d,]*(?:\.\d+)?/);
  if (!match) return NaN;
  return Number(match[0].replace(/,/g, ''));
}

async function callApi(path, method = 'GET', body = null) {
  const opts = { method, headers: {} };
  if (body) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const response = await fetch(`${VOIP_TOOL_API_BASE}${path}`, opts);
  let data;
  try {
    data = await response.json();
  } catch (err) {
    const text = await response.text();
    data = {
      ok: false,
      error: `Non-JSON response from API (status ${response.status})`,
      raw: (text || '').slice(0, 1000),
    };
  }
  return { status: response.status, data };
}

async function callAbsoluteApi(baseUrl, path, method = 'GET', body = null) {
  const opts = { method, headers: {} };
  if (body) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const response = await fetch(`${baseUrl}${path}`, opts);
  let data;
  try {
    data = await response.json();
  } catch (err) {
    const text = await response.text();
    data = {
      ok: false,
      error: `Non-JSON response from API (status ${response.status})`,
      raw: (text || '').slice(0, 1000),
    };
  }
  return { status: response.status, data };
}

function AccountPanel({ accountId, startDate, stopDate, onResult, onNotify }) {
  const [cookieHeader, setCookieHeader] = useState('');
  const title = accountId === 'account1' ? 'Edz-route-63-1' : 'Frek1-route-63';

  const runAction = async (action) => {
    try {
      let path = '';
      let method = 'POST';
      let body = null;

      if (action === 'open') path = `/api/account/${accountId}/browser/open`;
      if (action === 'saveSession') path = `/api/account/${accountId}/browser/save-session`;
      if (action === 'close') path = `/api/account/${accountId}/browser/close`;
      if (action === 'info') {
        method = 'GET';
        path = `/api/account/${accountId}/session/info`;
      }
      if (action === 'clear') path = `/api/account/${accountId}/session/clear`;
      if (action === 'fetch') {
        path = `/api/account/${accountId}/report/fetch`;
        body = { start_date: startDate, stop_date: stopDate };
      }
      if (action === 'setCookie') {
        path = `/api/account/${accountId}/session/set-cookie`;
        body = { cookie_header: cookieHeader.trim() };
      }

      const result = await callApi(path, method, body);
      onResult(result);
      onNotify(result.status >= 200 && result.status < 300 ? 'success' : 'error', `${title}: ${action} (${result.status})`);
    } catch (err) {
      onNotify('error', `${title}: ${action} failed (${err.message})`);
    }
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={1.25}>
          <Typography variant="h6">{title}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={() => runAction('open')}>Open Login Browser</Button>
            <Button variant="outlined" onClick={() => runAction('saveSession')}>Save Browser Session</Button>
            <Button variant="outlined" color="error" onClick={() => runAction('close')}>Close Browser</Button>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" onClick={() => runAction('info')}>Session Info</Button>
            <Button variant="outlined" onClick={() => runAction('fetch')}>Fetch Single</Button>
            <Button variant="outlined" color="error" onClick={() => runAction('clear')}>Clear Session</Button>
          </Stack>
          <Divider />
          <TextField
            label="Fallback Cookie Header (optional)"
            value={cookieHeader}
            onChange={(e) => setCookieHeader(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Button variant="text" onClick={() => runAction('setCookie')}>Set Cookie Header</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function VoipReportToolPage() {
  const [startDate, setStartDate] = useState(getToday());
  const [stopDate, setStopDate] = useState(getToday());
  const [smsUsername, setSmsUsername] = useState('');
  const [smsPassword, setSmsPassword] = useState('');
  const [result, setResult] = useState({});
  const [amountSummary, setAmountSummary] = useState('No fetch yet.');
  const [toast, setToast] = useState({ open: false, severity: 'info', message: '' });

  const notify = (severity, message) => setToast({ open: true, severity, message });

  const handleResult = (payload) => {
    setResult(payload);
    const data = payload?.data || payload;

    if (data?.account1 && data?.account2 && Object.prototype.hasOwnProperty.call(data, 'combined_total_cost')) {
      const a1 = toNumber(data.account1.total_cost, data.account1.total_cost_raw);
      const a2 = toNumber(data.account2.total_cost, data.account2.total_cost_raw);
      const combined = Number.isFinite(Number(data.combined_total_cost))
        ? Number(data.combined_total_cost)
        : ((Number.isFinite(a1) ? a1 : 0) + (Number.isFinite(a2) ? a2 : 0));
      setAmountSummary(`Amount Check -> Account1: $${formatAmount(a1)} | Account2: $${formatAmount(a2)} | Combined: $${formatAmount(combined)}`);
      return;
    }

    if (Object.prototype.hasOwnProperty.call(data || {}, 'total_cost')) {
      const single = toNumber(data.total_cost, data.total_cost_raw);
      setAmountSummary(`Amount Check -> ${data.account_id || 'Account'}: $${formatAmount(single)}${data.total_time ? ` | Total Time: ${data.total_time}` : ''}`);
      return;
    }

    if (data?.account1 && data?.account2 && Object.prototype.hasOwnProperty.call(data, 'combined_total_cost_usd')) {
      setAmountSummary(`Saved -> Account1: $${formatAmount(data.account1.total_cost_usd)} | Account2: $${formatAmount(data.account2.total_cost_usd)} | Combined: $${formatAmount(data.combined_total_cost_usd)}`);
      return;
    }

    if (Array.isArray(data?.results) && Object.prototype.hasOwnProperty.call(data, 'all_valid')) {
      const parts = data.results.map((item) => {
        const account = item?.account_id || 'account';
        const state = item?.valid ? 'VALID' : 'INVALID';
        const loginTag = item?.login_required ? 'login_required' : 'login_ok';
        return `${account}: ${state} (${loginTag})`;
      });
      setAmountSummary(
        `Cookie Check (${data.target_date || startDate}) -> ${data.all_valid ? 'ALL VALID' : 'NEEDS LOGIN'} | ${parts.join(' | ')}`,
      );
      return;
    }

    if (Object.prototype.hasOwnProperty.call(data || {}, 'total_amount')) {
      setAmountSummary(`SMS Amount Total -> €${formatAmount(data.total_amount)} | Rows: ${data.row_count ?? 0}`);
      return;
    }

    setAmountSummary('No amount data in this response.');
  };

  const rawResult = useMemo(() => JSON.stringify(result, null, 2), [result]);

  const fetchBoth = async () => {
    try {
      const response = await callApi('/api/report/fetch-both', 'POST', { start_date: startDate, stop_date: stopDate });
      handleResult(response);
      notify(response.status >= 200 && response.status < 300 ? 'success' : 'error', `Fetch both (${response.status})`);
    } catch (err) {
      notify('error', `Fetch both failed (${err.message})`);
    }
  };

  const saveBoth = async () => {
    if (!startDate || !stopDate || startDate !== stopDate) {
      notify('warning', 'Save Both requires same Start/Stop date.');
      return;
    }
    try {
      const response = await callApi('/api/earnings/save-both', 'POST', { date: startDate });
      handleResult(response);
      notify(response.status >= 200 && response.status < 300 ? 'success' : 'error', `Save both (${response.status})`);
    } catch (err) {
      notify('error', `Save both failed (${err.message})`);
    }
  };

  const fetchSmsTotal = async () => {
    if (!smsUsername.trim() || !smsPassword.trim()) {
      notify('warning', 'SMS username and password are required.');
      return;
    }
    try {
      const response = await callApi('/api/sms/report/fetch', 'POST', {
        username: smsUsername.trim(),
        password: smsPassword,
        start_date: startDate,
        stop_date: stopDate,
      });
      handleResult(response);
      notify(response.status >= 200 && response.status < 300 ? 'success' : 'error', `SMS fetch (${response.status})`);
    } catch (err) {
      notify('error', `SMS fetch failed (${err.message})`);
    }
  };

  const validateCookies = async () => {
    try {
      const dateParam = startDate ? `?date=${encodeURIComponent(startDate)}` : '';
      const response = await callAbsoluteApi(VOIP_AUTO_API_BASE, `/api/voip/cookies/validate${dateParam}`, 'GET');
      handleResult(response);
      const isHttpOk = response.status >= 200 && response.status < 300;
      const allValid = response?.data?.all_valid === true;
      if (isHttpOk && allValid) {
        notify('success', `Cookie validity check passed (${response.status})`);
      } else if (isHttpOk) {
        notify('warning', `Cookie validity check failed (${response.status})`);
      } else {
        notify('error', `Cookie validity check failed (${response.status})`);
      }
    } catch (err) {
      notify('error', `Cookie validity check failed (${err.message})`);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(18, 18, 22, 0.75)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>VOIP Report Tool</Typography>
                  <Chip label="React + MUI" color="primary" variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Login each account, save browser session, check cookie validity, fetch both reports, then save both earnings.
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="date"
                      label="Start Date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      sx={{ '& .MuiInputBase-root': { background: 'rgba(13, 14, 18, 0.8)' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="date"
                      label="Stop Date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={stopDate}
                      onChange={(e) => setStopDate(e.target.value)}
                      sx={{ '& .MuiInputBase-root': { background: 'rgba(13, 14, 18, 0.8)' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button variant="contained" onClick={fetchBoth}>Fetch Both Reports</Button>
                      <Button variant="outlined" onClick={saveBoth}>Save Both To Earnings</Button>
                      <Button variant="outlined" color="warning" onClick={validateCookies}>Check Cookie Validity</Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <AccountPanel
                  accountId="account1"
                  startDate={startDate}
                  stopDate={stopDate}
                  onResult={handleResult}
                  onNotify={notify}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AccountPanel
                  accountId="account2"
                  startDate={startDate}
                  stopDate={stopDate}
                  onResult={handleResult}
                  onNotify={notify}
                />
              </Grid>
            </Grid>

            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(18, 18, 22, 0.75)',
              }}
            >
              <Stack spacing={1.25}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>SMS Test Fetch (No Captcha)</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="SMS Username"
                      value={smsUsername}
                      onChange={(e) => setSmsUsername(e.target.value)}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { background: 'rgba(13, 14, 18, 0.8)' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      type="password"
                      label="SMS Password"
                      value={smsPassword}
                      onChange={(e) => setSmsPassword(e.target.value)}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { background: 'rgba(13, 14, 18, 0.8)' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button variant="contained" fullWidth sx={{ height: '100%' }} onClick={fetchSmsTotal}>
                      Fetch SMS Amount Total
                    </Button>
                  </Grid>
                </Grid>
              </Stack>
            </Paper>

            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(18, 18, 22, 0.75)',
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Amount Summary</Typography>
              <Alert severity="info" sx={{ mb: 1.5 }}>{amountSummary}</Alert>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Raw Result</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: '#06080f',
                  color: '#eaf1ff',
                  border: '1px solid rgba(144, 202, 249, 0.35)',
                  overflow: 'auto',
                  maxHeight: 520,
                  fontSize: 13,
                  lineHeight: 1.45,
                  fontFamily: '"Cascadia Code", Consolas, "Courier New", monospace',
                  whiteSpace: 'pre',
                }}
              >
                {rawResult}
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={toast.open}
            autoHideDuration={2800}
            onClose={() => setToast((s) => ({ ...s, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              severity={toast.severity}
              variant="filled"
              onClose={() => setToast((s) => ({ ...s, open: false }))}
              sx={{ width: '100%' }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

